import React, { useState } from "react";
import axios from "axios";
import { useAuth, purgePreviewClientState } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check for session expiry message
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === 'true') {
      setError("Your session expired. Please sign in again.");
      // Clean up URL
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  // Defensive: the login page must never inherit a preview session. If a lingering
  // preview/self token cookie is detected, drop it server-side (logout) and purge any
  // client preview keys — so the read-only guard can never trap "Sign In".
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      purgePreviewClientState();
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!cancelled && (data?.active_view === "preview_employee" || data?.active_view === "self_employee")) {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          purgePreviewClientState();
        }
      } catch (_) {
        // 401 (no session) is expected on the login page — nothing to clear.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate("/");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="mb-4">
            <h1 className="text-3xl font-medium text-foreground">GT L.A.B Careers</h1>
            <p className="text-sm text-muted-foreground mt-1">GT Lash & Brows</p>
          </div>
          <CardTitle className="text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to access your employee dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Username or Email</Label>
              <Input
                id="email"
                type="text"
                placeholder="ashleya"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}